import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as FocusEvent_,
  type KeyboardEvent as KeyboardEvent_,
  type MouseEvent as MouseEvent_,
  type PointerEvent as PointerEvent_,
} from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LOCALES } from '@ceo/shared';
import { switchLocalePath, useLocale, useLocalePath } from '@/lib/locale';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrolled } from '@/hooks/useScrolled';
import { Icon } from '@/components/ui/Icon';
import { NAV, type NavItem } from './nav';
import styles from './Header.module.css';

const LANG_LABEL = { en: 'EN', sq: 'AL' } as const;
const LANG_NAME = { en: 'English', sq: 'Shqip' } as const;

export function Header({ overlay }: { overlay: boolean }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const to = useLocalePath();
  const { pathname } = useLocation();
  const scrolled = useScrolled();
  // The mobile menu remembers the path it was opened on, so navigating closes it
  // without a state-syncing effect.
  const [openOn, setOpenOn] = useState<string | null>(null);
  // The phone menu only exists below the desktop breakpoint (same as Header.module.css).
  // If the screen grows past it while the menu is open (e.g. a tablet rotated to landscape),
  // close the menu so its scroll lock is released — adjusted during render, not in an effect.
  const compact = useMediaQuery('(max-width: 991px)');
  if (!compact && openOn !== null) setOpenOn(null);
  const open = openOn === pathname;
  // Phone accordion: sections the user toggled. Untouched sections follow the route, so the
  // one you're in starts expanded without syncing state in an effect.
  const [toggled, setToggled] = useState<Partial<Record<NavItem['key'], boolean>>>({});
  const menuId = useId();

  // While the phone menu is open: Escape closes it and the page behind doesn't scroll.
  // Both touch the browser outside React, which is what effects are for.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenOn(null);
    const { overflow } = document.body.style;
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open]);

  const bare = pathname.replace(/^\/sq(?=\/|$)/, '') || '/';
  const isActive = (item: NavItem) =>
    item.match.some((m) => (m === '/' ? bare === '/' : bare === m || bare.startsWith(`${m}/`)));
  const isExpanded = (item: NavItem) => toggled[item.key] ?? isActive(item);

  /*
   * Desktop dropdowns: one open at a time, opened by mouse hover, keyboard focus, or a click /
   * tap on its button (iPad, touch laptops). Like the phone menu it remembers the path it was
   * opened on, so navigating closes it. The visible dropdown and the button's aria-expanded
   * both come from this one value, so screen readers always hear the real state.
   */
  const [drop, setDrop] = useState<{ key: NavItem['key']; on: string } | null>(null);
  const dropKey = !compact && drop?.on === pathname ? drop.key : null;
  const openDrop = (key: NavItem['key']) => setDrop({ key, on: pathname });
  /**
   * When the last click/tap happened (or when we moved focus ourselves). Focus arriving soon
   * after it isn't keyboard focus — on touch screens it even comes after the finger lifts.
   */
  const pointerAt = useRef(-Infinity);
  const lastPointer = useRef<string>('mouse');

  // A click or tap anywhere outside the open dropdown closes it.
  useEffect(() => {
    if (!dropKey) return;
    const onDown = (e: PointerEvent) => {
      if (!(e.target as Element).closest?.(`[data-drop="${dropKey}"]`)) setDrop(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [dropKey]);

  const dropHandlers = (key: NavItem['key']) =>
    compact
      ? {}
      : {
          'data-drop': key,
          onPointerEnter: (e: PointerEvent_) => e.pointerType === 'mouse' && openDrop(key),
          onPointerLeave: (e: PointerEvent_) =>
            e.pointerType === 'mouse' && dropKey === key && setDrop(null),
          onPointerDown: (e: PointerEvent_) => {
            pointerAt.current = e.timeStamp;
            lastPointer.current = e.pointerType;
          },
          // Reaching the button (or a link inside) with Tab opens the dropdown.
          onFocus: (e: FocusEvent_) => e.timeStamp - pointerAt.current > 800 && openDrop(key),
          // Tabbing out of the item closes it.
          onBlur: (e: FocusEvent_) => {
            if (dropKey === key && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setDrop(null);
            }
          },
          onKeyDown: (e: KeyboardEvent_) => {
            if (e.key !== 'Escape' || dropKey !== key) return;
            setDrop(null);
            pointerAt.current = e.timeStamp; // focusing the button shouldn't reopen it
            e.currentTarget.querySelector('button')?.focus();
          },
        };

  const onSectionButton = (item: NavItem, e: MouseEvent_) => {
    if (compact) {
      setToggled((t) => ({ ...t, [item.key]: !isExpanded(item) }));
    } else if (e.detail > 0 && lastPointer.current === 'mouse') {
      openDrop(item.key); // a mouse is already hovering it: a click just keeps it open
    } else {
      setDrop(dropKey === item.key ? null : { key: item.key, on: pathname }); // keyboard / touch
    }
  };

  // Any link in the header closes the menus right away: the address only changes once the next
  // page has loaded (a moment on a slow connection), and not at all for the page you're on.
  const closeMenus = () => {
    setOpenOn(null);
    setDrop(null);
  };

  const solid = !overlay || scrolled || open;

  return (
    <header
      className={`${styles.header} ${solid ? styles.solid : ''} ${scrolled ? styles.scrolled : ''}`}
    >
      {open && (
        <div className={styles.backdrop} aria-hidden="true" onClick={() => setOpenOn(null)} />
      )}
      <div className={`container ${styles.wrap}`}>
        <div className={styles.bar}>
          <Link
            to={to('/')}
            className={styles.logo}
            aria-label={t('nav.home')}
            onClick={closeMenus}
          >
            <img src="/media/ceo_logo.png" alt={t('brand')} width={259} height={108} />
          </Link>

          <nav id={menuId} className={`${styles.nav} ${open ? styles.navOpen : ''}`}>
            <ul className={styles.menu}>
              {NAV.map((item) => (
                <li
                  key={item.key}
                  className={
                    item.children
                      ? `${styles.hasChildren} ${isExpanded(item) ? styles.subOpen : ''} ${
                          dropKey === item.key ? styles.dropOpen : ''
                        }`
                      : undefined
                  }
                  {...(item.children ? dropHandlers(item.key) : {})}
                >
                  {item.to ? (
                    <Link
                      to={to(item.to)}
                      className={isActive(item) ? styles.active : undefined}
                      aria-current={isActive(item) && bare === item.to ? 'page' : undefined}
                      onClick={closeMenus}
                    >
                      {t(`nav.${item.key}`)}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className={isActive(item) ? styles.active : undefined}
                      aria-expanded={compact ? isExpanded(item) : dropKey === item.key}
                      aria-controls={`${menuId}-${item.key}`}
                      onClick={(e) => onSectionButton(item, e)}
                    >
                      {t(`nav.${item.key}`)}
                      <Icon name="chevron-down" className={styles.caret} />
                    </button>
                  )}
                  {item.children && (
                    <div className={styles.subWrap}>
                      <ul id={`${menuId}-${item.key}`} className={styles.submenu}>
                        {item.children.map((c) => (
                          <li key={c.key}>
                            <Link
                              to={to(c.to)}
                              onClick={closeMenus}
                              aria-current={
                                bare === c.to || bare.startsWith(`${c.to}/`) ? 'page' : undefined
                              }
                            >
                              {t(`nav.${c.key}`)}
                              <Icon name="arrow-right" className={styles.subArrow} />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <ul className={styles.langs} aria-label={t('nav.language')}>
            {[...LOCALES].reverse().map((l) => (
              <li key={l}>
                <Link
                  to={switchLocalePath(pathname, l)}
                  hrefLang={l}
                  lang={l}
                  aria-label={LANG_NAME[l]}
                  aria-current={l === locale ? 'true' : undefined}
                  onClick={closeMenus}
                >
                  {LANG_LABEL[l]}
                </Link>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={styles.burger}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpenOn(open ? null : pathname)}
          >
            <span className="visually-hidden">{t('nav.menu')}</span>
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
}
